import {
  Box,
  Card,
  CardContent,
  Divider,
  Stack,
  type SxProps,
  type Theme,
  Typography,
} from '@mui/material';
import type { ReactNode } from 'react';

export interface SectionCardProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  /** Leading icon rendered beside the title. */
  icon?: ReactNode;
  /** Right-aligned header actions (e.g. an "Edit" button). */
  action?: ReactNode;
  children: ReactNode;
  /** Draw a divider between the header and body. */
  divider?: boolean;
  /** Remove content padding (for tables / lists that manage their own insets). */
  disableContentPadding?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * The standard titled content card used across profile, dashboard and detail screens: a clean
 * header (optional icon + title + subtitle + action) over a soft-elevated surface. Standardizing on
 * this keeps card headers, spacing and radii consistent instead of each screen hand-rolling a
 * Card/CardContent/Typography stack (docs/development/FrontendGuidelines.md §7.1).
 */
export function SectionCard({
  title,
  subtitle,
  icon,
  action,
  children,
  divider = false,
  disableContentPadding = false,
  sx,
}: SectionCardProps) {
  const hasHeader = title || subtitle || action || icon;

  return (
    <Card sx={sx}>
      {hasHeader ? (
        <Box sx={{ px: 3, pt: 3, pb: subtitle ? 2 : 2.5 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
              {icon ? (
                <Box sx={{ color: 'primary.main', display: 'flex', '& svg': { fontSize: 22 } }}>
                  {icon}
                </Box>
              ) : null}
              <Box sx={{ minWidth: 0 }}>
                {title ? (
                  <Typography variant="h6" component="h2">
                    {title}
                  </Typography>
                ) : null}
                {subtitle ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    {subtitle}
                  </Typography>
                ) : null}
              </Box>
            </Stack>
            {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
          </Stack>
        </Box>
      ) : null}
      {divider && hasHeader ? <Divider /> : null}
      {disableContentPadding ? (
        children
      ) : (
        <CardContent sx={hasHeader ? { pt: divider ? 3 : 0 } : undefined}>{children}</CardContent>
      )}
    </Card>
  );
}
